// Sign-off Documents — single page, role-aware.
//
//   Everyone      → "My documents": open each doc in the view-only in-app
//                   reader, then mark it as read (timestamped). The sign-off
//                   button stays locked until the document has been opened.
//   Admin / HR    → additionally manage the library (publish/remove/download)
//                   and read the acknowledgement matrix: employees × documents.
//
// Files are never downloadable by members — they stream through an
// authenticated endpoint into the canvas-based reader (PdfViewerModal).
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  FileCheck2,
  Upload,
  Trash2,
  Eye,
  Download,
  CheckCircle2,
  Clock4,
  Users,
  FolderOpen,
  Lock,
  Minus,
} from "lucide-react";
import { API_URL } from "../../api/api";
import PdfViewerModal from "./PdfViewerModal";
import "./signoff.css";
import { PageHeader } from "../../components/em/ui";

import { confirmDialog } from "../../components/em/confirm";
import { toast } from "../../utils/toast";
import { hasPermission } from "../../utils/permissionUtils";
const auth = () => {
  const token = localStorage.getItem("token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

const fileUrl = (p) => {
  if (!p) return null;
  if (/^https?:\/\//.test(p)) return p;
  return `${API_URL}${p.startsWith("/") ? "" : "/"}${p}`;
};

const CATEGORY_LABELS = {
  onboarding: "Onboarding",
  hr_manual: "HR Manual",
  other: "Other information",
};

const fmtTs = (ts) =>
  ts
    ? new Date(ts).toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

const fmtDate = (ts) =>
  ts
    ? new Date(ts).toLocaleDateString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : null;

export default function SignOffDocumentsPage() {
  // Library, upload and acknowledgement report — `manage_signoff`, the key the
  // /api/signoff management routes enforce.
  const isManager = hasPermission("manage_signoff");

  const [tab, setTab] = useState("mine"); // mine | manage | report
  const [myDocs, setMyDocs] = useState([]);
  const [library, setLibrary] = useState([]);
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  // Reader state: which doc is open, and which docs have been opened this
  // session (unlocks "Mark as read").
  const [readerDoc, setReaderDoc] = useState(null);
  const [viewedIds, setViewedIds] = useState(() => new Set());

  // Upload form state
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("onboarding");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState(null);
  const fileRef = useRef(null);

  const loadMine = async () => {
    const res = await axios.get(`${API_URL}/api/signoff/documents`, auth());
    setMyDocs(res.data || []);
  };
  const loadLibrary = async () => {
    const res = await axios.get(
      `${API_URL}/api/signoff/documents?all=1`,
      auth(),
    );
    setLibrary(res.data || []);
  };
  const loadReport = async () => {
    const res = await axios.get(`${API_URL}/api/signoff/report`, auth());
    setReport(res.data || []);
  };

  const refreshAll = async () => {
    setLoading(true);
    try {
      await Promise.all(
        [
          loadMine(),
          isManager && loadLibrary(),
          isManager && loadReport(),
        ].filter(Boolean),
      );
    } catch (e) {
      console.warn("[signoff] load failed", e?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markViewed = (docId) =>
    setViewedIds((prev) => {
      if (prev.has(docId)) return prev;
      const next = new Set(prev);
      next.add(docId);
      return next;
    });

  const acknowledge = async (doc) => {
    setBusyId(doc.id);
    try {
      await axios.post(
        `${API_URL}/api/signoff/documents/${doc.id}/acknowledge`,
        {},
        auth(),
      );
      await Promise.all(
        [
          loadMine(),
          isManager && loadLibrary(),
          isManager && loadReport(),
        ].filter(Boolean),
      );
    } catch (e) {
      toast.error(e?.response?.data?.error || "Could not record your sign-off.");
    } finally {
      setBusyId(null);
    }
  };

  const uploadDoc = async (e) => {
    e.preventDefault();
    if (!title.trim() || !file) {
      setUploadErr("A title and a PDF file are both required.");
      return;
    }
    setUploading(true);
    setUploadErr(null);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("category", category);
      fd.append("description", description.trim());
      fd.append("file", file);
      await axios.post(`${API_URL}/api/signoff/documents`, fd, auth());
      setTitle("");
      setDescription("");
      setCategory("onboarding");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      await refreshAll();
    } catch (err) {
      setUploadErr(err?.response?.data?.error || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const removeDoc = async (doc) => {
    if (!(await confirmDialog(
      `Remove "${doc.title}"? Employees will no longer see it; existing sign-offs are kept.`
    )))
      return;
    setBusyId(doc.id);
    try {
      await axios.delete(`${API_URL}/api/signoff/documents/${doc.id}`, auth());
      await refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Could not remove the document.");
    } finally {
      setBusyId(null);
    }
  };

  // Admin/HR only — fetch the original through the authed endpoint.
  const downloadDoc = async (doc) => {
    setBusyId(doc.id);
    try {
      const res = await axios.get(
        `${API_URL}/api/signoff/documents/${doc.id}/download`,
        { ...auth(), responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_name || `${doc.title}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download the document.");
    } finally {
      setBusyId(null);
    }
  };

  /* Report rows → employees × documents matrix */
  const matrix = useMemo(() => {
    const docMap = new Map(); // document_id → {id, title, category}
    const empMap = new Map(); // member_id → {…, acks: Map<docId, ts>}
    for (const r of report) {
      if (!docMap.has(r.document_id)) {
        docMap.set(r.document_id, {
          id: r.document_id,
          title: r.document_title,
          category: r.category,
        });
      }
      if (!empMap.has(r.member_id)) {
        empMap.set(r.member_id, {
          member_id: r.member_id,
          name: r.employee_name || "—",
          designation: r.designation,
          department: r.department,
          photo: r.profile_photo,
          acks: new Map(),
        });
      }
      empMap.get(r.member_id).acks.set(r.document_id, r.acknowledged_at);
    }
    return { docs: [...docMap.values()], employees: [...empMap.values()] };
  }, [report]);

  const pendingCount = myDocs.filter((d) => !d.my_acknowledged_at).length;

  /* ── Renderers ─────────────────────────────────────────────────────── */

  const renderMyDocs = () => (
    <div className="so-card">
      <p className="so-card-sub">
        {myDocs.length === 0
          ? "No documents have been published yet."
          : pendingCount === 0
            ? "You're up to date — every document has been signed off. 🎉"
            : `${pendingCount} document${pendingCount > 1 ? "s" : ""} awaiting your sign-off. Open a document to unlock its sign-off button.`}
      </p>
      <div className="so-doc-list">
        {myDocs.map((d) => {
          const acked = !!d.my_acknowledged_at;
          const viewed = viewedIds.has(d.id);
          return (
            <div key={d.id} className={`so-doc${acked ? " is-acked" : ""}`}>
              <span
                className={`so-doc-tile${acked ? " is-acked" : ""}`}
                aria-hidden="true"
              >
                {acked ? <CheckCircle2 size={18} /> : <Clock4 size={18} />}
              </span>
              <div className="so-doc-body">
                <p className="so-doc-title">{d.title}</p>
                <div className="so-doc-meta">
                  <span className="so-chip">
                    {CATEGORY_LABELS[d.category] || d.category}
                  </span>
                  {acked && (
                    <span className="so-doc-ackedat">
                      Signed off {fmtTs(d.my_acknowledged_at)}
                    </span>
                  )}
                </div>
                {d.description && (
                  <p className="so-doc-desc">{d.description}</p>
                )}
              </div>
              <div className="so-doc-actions">
                <button
                  type="button"
                  className="so-btn so-btn--ghost"
                  onClick={() => setReaderDoc(d)}
                >
                  <Eye size={14} /> Read
                </button>
                {!acked && (
                  <button
                    type="button"
                    className="so-btn so-btn--primary"
                    disabled={busyId === d.id || !viewed}
                    title={viewed ? undefined : "Open the document first"}
                    onClick={() => acknowledge(d)}
                  >
                    {busyId === d.id ? (
                      "Saving…"
                    ) : viewed ? (
                      <>Mark as read</>
                    ) : (
                      <>
                        <Lock size={12} /> Mark as read
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderManage = () => (
    <>
      <div className="so-card" style={{ marginBottom: 16 }}>
        <form className="so-upload" onSubmit={uploadDoc}>
          <h3 className="so-section-title">
            <Upload size={15} /> Publish a document
          </h3>
          {uploadErr && <div className="so-error">{uploadErr}</div>}
          <div className="so-upload-grid">
            <label className="so-field">
              <span>Title *</span>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Employee Onboarding Pack"
              />
            </label>
            <label className="so-field">
              <span>Category *</span>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="onboarding">Onboarding</option>
                <option value="hr_manual">HR Manual</option>
                <option value="other">Other information</option>
              </select>
            </label>
            <label className="so-field so-field--full">
              <span>Description</span>
              <input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="One line shown to employees under the title (optional)"
              />
            </label>
            <label className="so-field so-field--full">
              <span>Document file * (PDF, max 15MB)</span>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </label>
          </div>
          <button
            type="submit"
            className="so-btn so-btn--primary"
            disabled={uploading}
          >
            {uploading ? "Publishing…" : "Publish document"}
          </button>
        </form>
      </div>

      <div className="so-card so-card--flush">
        <h3 className="so-section-title so-section-title--pad">
          <FolderOpen size={15} /> Library
        </h3>
        {library.length === 0 ? (
          <div className="so-empty">
            Nothing published yet — use the form above to publish your first
            document.
          </div>
        ) : (
          <div className="so-tablewrap">
            <table className="so-table">
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Category</th>
                  <th>Sign-offs</th>
                  <th>Published</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {library.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <span className="so-cell-title">{d.title}</span>
                      {d.description && (
                        <span className="so-cell-sub">{d.description}</span>
                      )}
                    </td>
                    <td>
                      <span className="so-chip">
                        {CATEGORY_LABELS[d.category] || d.category}
                      </span>
                    </td>
                    <td>{d.ack_count}</td>
                    <td>
                      <span
                        className="so-cell-sub"
                        style={{ display: "block" }}
                      >
                        {fmtDate(d.created_at)}
                        {d.uploaded_by_name ? ` · ${d.uploaded_by_name}` : ""}
                      </span>
                    </td>
                    <td>
                      <div className="so-row-actions">
                        <button
                          type="button"
                          className="so-iconbtn"
                          title="Read in app"
                          onClick={() => setReaderDoc(d)}
                        >
                          <Eye size={15} />
                        </button>
                        <button
                          type="button"
                          className="so-iconbtn"
                          title="Download original"
                          disabled={busyId === d.id}
                          onClick={() => downloadDoc(d)}
                        >
                          <Download size={15} />
                        </button>
                        <button
                          type="button"
                          className="so-iconbtn so-iconbtn--danger"
                          title="Remove (existing sign-offs are kept)"
                          disabled={busyId === d.id}
                          onClick={() => removeDoc(d)}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );

  const renderReport = () => {
    const { docs, employees } = matrix;
    return (
      <div className="so-card so-card--flush">
        <p className="so-card-sub so-card-sub--pad">
          Every member/HR account against every published document, showing when
          each one was signed off.
        </p>
        {employees.length === 0 || docs.length === 0 ? (
          <div className="so-empty">
            No employees or documents to report on yet.
          </div>
        ) : (
          <div className="so-tablewrap">
            <table className="so-table so-matrix">
              <thead>
                <tr>
                  <th className="so-matrix-emp">Employee</th>
                  {docs.map((d) => (
                    <th key={d.id} className="so-matrix-doc" title={d.title}>
                      <span>{d.title}</span>
                    </th>
                  ))}
                  <th className="so-matrix-progress">Progress</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((emp) => {
                  const done = docs.filter((d) => emp.acks.get(d.id)).length;
                  const complete = done === docs.length;
                  return (
                    <tr key={emp.member_id}>
                      <td className="so-matrix-emp">
                        <span className="so-emp-avatar" aria-hidden="true">
                          {emp.photo ? (
                            <img src={fileUrl(emp.photo)} alt="" />
                          ) : (
                            (emp.name || "?").slice(0, 1).toUpperCase()
                          )}
                        </span>
                        <span className="so-emp-id">
                          <span className="so-cell-title">{emp.name}</span>
                          <span className="so-cell-sub">
                            {[emp.designation, emp.department]
                              .filter(Boolean)
                              .join(" · ") || "—"}
                          </span>
                        </span>
                      </td>
                      {docs.map((d) => {
                        const ts = emp.acks.get(d.id);
                        return (
                          <td key={d.id} className="so-matrix-cell">
                            {ts ? (
                              // The sign-off time itself, rather than a tick
                              // whose timestamp was only reachable on hover —
                              // unusable on touch, and invisible when scanning
                              // or exporting the table.
                              <span
                                className="so-signed-at"
                                title={`Signed ${fmtTs(ts)}`}
                              >
                                {fmtTs(ts)}
                              </span>
                            ) : (
                              <span className="so-tick" title="Pending">
                                <Minus size={13} strokeWidth={3} />
                              </span>
                            )}
                          </td>
                        );
                      })}
                      <td className="so-matrix-progress">
                        <span
                          className={`so-progress${complete ? " is-complete" : ""}`}
                        >
                          {done}/{docs.length}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="contentWrapper">
      <PageHeader
        icon={FileCheck2}
        title="Sign-off Documents"
        subtitle="Company documents every joiner reads and signs off on — view-only, in the app."
      />
      <div className="so-page">
        {/* <div className="so-head">
          <span className="so-head-tile" aria-hidden="true">
            <FileCheck2 size={20} />
          </span>
          <div>
            <h1>Sign-off Documents</h1>
            <p>
              Company documents every joiner reads and signs off on — view-only,
              in the app.
            </p>
          </div>
        </div> */}

        {isManager && (
          <div className="so-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === "mine"}
              className={`so-tab${tab === "mine" ? " is-active" : ""}`}
              onClick={() => setTab("mine")}
            >
              <FileCheck2 size={14} /> My documents
              {pendingCount > 0 && (
                <span className="so-tab-count">{pendingCount}</span>
              )}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "manage"}
              className={`so-tab${tab === "manage" ? " is-active" : ""}`}
              onClick={() => setTab("manage")}
            >
              <Upload size={14} /> Manage
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "report"}
              className={`so-tab${tab === "report" ? " is-active" : ""}`}
              onClick={() => setTab("report")}
            >
              <Users size={14} /> Acknowledgements
            </button>
          </div>
        )}

        {loading ? (
          <div className="so-card">
            <div className="so-empty">Loading…</div>
          </div>
        ) : tab === "manage" && isManager ? (
          renderManage()
        ) : tab === "report" && isManager ? (
          renderReport()
        ) : (
          renderMyDocs()
        )}

        {readerDoc && (
          <PdfViewerModal
            doc={readerDoc}
            onClose={() => setReaderDoc(null)}
            onViewed={markViewed}
          />
        )}
      </div>
    </div>
  );
}
